import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  DatePicker,
  Checkbox,
  Popconfirm,
} from 'antd';
import { PlusOutlined, ToolOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  useInstalledEquipment,
  useCreateInstalledEquipment,
  useUpdateInstalledEquipment,
  useDeleteInstalledEquipment,
} from '@hooks/useService';
import type {
  InstalledEquipmentItem,
  InstalledEquipmentFilters,
  EquipmentStatus,
} from '@/types/service';
import CreateServiceRequestModal from './CreateServiceRequestModal';

const MD_STATUS_OPTIONS: { value: EquipmentStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Active', label: 'Активне' },
  { value: 'UnderMaintenance', label: 'На обслуговуванні' },
  { value: 'Decommissioned', label: 'Виведене' },
  { value: 'Recalled', label: 'Відкликане' },
];

const MD_STATUS_COLOR_MAP: Record<EquipmentStatus, string> = {
  Active: 'green',
  UnderMaintenance: 'orange',
  Decommissioned: 'default',
  Recalled: 'red',
};

const MD_STATUS_LABEL_MAP: Record<EquipmentStatus, string> = {
  Active: 'Активне',
  UnderMaintenance: 'На обслуговуванні',
  Decommissioned: 'Виведене',
  Recalled: 'Відкликане',
};

// timestamptz на сервері приймає лише UTC: дата без часу дала б DateTime Kind=Unspecified
const mdToUtcDate = (mdDate: Dayjs): string => `${mdDate.format('YYYY-MM-DD')}T00:00:00Z`;

interface InstalledEquipmentPageProps {
  // Викликається після створення заявки — щоб відкрити її картку
  onServiceRequestCreated: (mdRequestId: string) => void;
}

export default function InstalledEquipmentPage({
  onServiceRequestCreated,
}: InstalledEquipmentPageProps) {
  const [mdRequestEquipment, setMdRequestEquipment] = useState<InstalledEquipmentItem | null>(null);
  const [mdSearch, setMdSearch] = useState('');
  const [mdStatusFilter, setMdStatusFilter] = useState<EquipmentStatus | ''>('');
  const [mdWarrantyExpiring, setMdWarrantyExpiring] = useState(false);
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdModalOpen, setMdModalOpen] = useState(false);
  const [mdEditingItem, setMdEditingItem] = useState<InstalledEquipmentItem | null>(null);

  const [mdForm] = Form.useForm();

  const mdFilters: InstalledEquipmentFilters = useMemo(() => ({
    search: mdSearch || undefined,
    status: mdStatusFilter || undefined,
    warrantyExpiring: mdWarrantyExpiring || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdStatusFilter, mdWarrantyExpiring, mdPage, mdPageSize]);

  const { data: mdEquipmentData, isLoading: mdIsLoading } = useInstalledEquipment(mdFilters);
  const mdCreateMutation = useCreateInstalledEquipment();
  const mdUpdateMutation = useUpdateInstalledEquipment();
  const mdDeleteMutation = useDeleteInstalledEquipment();

  const mdHandleOpenCreate = () => {
    setMdEditingItem(null);
    mdForm.resetFields();
    setMdModalOpen(true);
  };

  const mdHandleOpenEdit = (mdRecord: InstalledEquipmentItem) => {
    setMdEditingItem(mdRecord);
    mdForm.setFieldsValue({
      serialNumber: mdRecord.serialNumber,
      productId: mdRecord.productId,
      customerId: mdRecord.customerId,
      installedAt: mdRecord.installedAt ? dayjs(mdRecord.installedAt) : undefined,
      warrantyExpiresAt: mdRecord.warrantyExpiresAt ? dayjs(mdRecord.warrantyExpiresAt) : undefined,
      installationAddress: mdRecord.installationAddress,
      status: mdRecord.status,
      nextMaintenanceDue: mdRecord.nextMaintenanceDue ? dayjs(mdRecord.nextMaintenanceDue) : undefined,
    });
    setMdModalOpen(true);
  };

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();

    if (mdEditingItem) {
      // Статус обов'язковий: без нього сервер скинув би його до Active
      await mdUpdateMutation.mutateAsync({
        id: mdEditingItem.id,
        installationAddress: mdValues.installationAddress,
        warrantyExpiresAt: mdToUtcDate(mdValues.warrantyExpiresAt),
        status: mdValues.status,
        nextMaintenanceDue: mdValues.nextMaintenanceDue ? mdToUtcDate(mdValues.nextMaintenanceDue) : undefined,
      });
    } else {
      await mdCreateMutation.mutateAsync({
        serialNumber: mdValues.serialNumber,
        productId: mdValues.productId,
        customerId: mdValues.customerId,
        installationAddress: mdValues.installationAddress,
        installedAt: mdToUtcDate(mdValues.installedAt),
        warrantyExpiresAt: mdToUtcDate(mdValues.warrantyExpiresAt),
      });
    }
    setMdModalOpen(false);
  };

  const mdGetRowClassName = (mdRecord: InstalledEquipmentItem): string => {
    if (!mdRecord.warrantyExpiresAt) return '';
    const mdDaysLeft = dayjs(mdRecord.warrantyExpiresAt).diff(dayjs(), 'day');
    if (mdDaysLeft < 0) return 'row-expired';
    if (mdDaysLeft < 90) return 'row-expiring';
    return '';
  };

  const mdColumns: ColumnsType<InstalledEquipmentItem> = [
    {
      title: 'Серійний номер',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 160,
      sorter: (mdA, mdB) => mdA.serialNumber.localeCompare(mdB.serialNumber),
    },
    {
      title: 'Продукт',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: 'Встановлено',
      dataIndex: 'installedAt',
      key: 'installedAt',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
    },
    {
      title: 'Гарантія до',
      dataIndex: 'warrantyExpiresAt',
      key: 'warrantyExpiresAt',
      width: 120,
      render: (mdValue: string | null) =>
        mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (mdValue: EquipmentStatus) => (
        <Tag color={MD_STATUS_COLOR_MAP[mdValue]}>
          {MD_STATUS_LABEL_MAP[mdValue] ?? mdValue}
        </Tag>
      ),
    },
    {
      title: 'Наступне ТО',
      dataIndex: 'nextMaintenanceDue',
      key: 'nextMaintenanceDue',
      width: 120,
      render: (mdValue: string | null) =>
        mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—',
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 220,
      render: (_: unknown, mdRecord: InstalledEquipmentItem) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => mdHandleOpenEdit(mdRecord)}
          >
            Редагувати
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ToolOutlined />}
            onClick={() => setMdRequestEquipment(mdRecord)}
          >
            Створити заявку
          </Button>
          <Popconfirm
            title="Видалити обладнання?"
            onConfirm={() => mdDeleteMutation.mutate(mdRecord.id)}
            okText="Так"
            cancelText="Ні"
          >
            <Button type="link" size="small" danger>
              Видалити
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .row-expired { background-color: #fff1f0 !important; }
        .row-expired:hover > td { background-color: #ffccc7 !important; }
        .row-expiring { background-color: #fffbe6 !important; }
        .row-expiring:hover > td { background-color: #fff1b8 !important; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Встановлене обладнання
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
          Додати обладнання
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за серійним номером..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 260 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 200 }}
        />
        <Checkbox
          checked={mdWarrantyExpiring}
          onChange={(mdE) => {
            setMdWarrantyExpiring(mdE.target.checked);
            setMdPage(1);
          }}
        >
          Гарантія закінчується
        </Checkbox>
      </Space>

      <Table<InstalledEquipmentItem>
        columns={mdColumns}
        dataSource={mdEquipmentData?.items}
        rowKey="id"
        loading={mdIsLoading}
        rowClassName={mdGetRowClassName}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdEquipmentData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <CreateServiceRequestModal
        equipment={mdRequestEquipment}
        onClose={() => setMdRequestEquipment(null)}
        onCreated={(mdRequestId) => {
          setMdRequestEquipment(null);
          onServiceRequestCreated(mdRequestId);
        }}
      />

      <Modal
        title={mdEditingItem ? 'Редагувати обладнання' : 'Додати обладнання'}
        open={mdModalOpen}
        onOk={mdHandleSubmit}
        onCancel={() => setMdModalOpen(false)}
        confirmLoading={mdCreateMutation.isPending || mdUpdateMutation.isPending}
        okText={mdEditingItem ? 'Зберегти' : 'Додати'}
        cancelText="Скасувати"
        width={600}
      >
        {/* Виріб, клієнт, серійний номер і дату встановлення сервер при оновленні не змінює */}
        <Form form={mdForm} layout="vertical">
          <Form.Item
            name="serialNumber"
            label="Серійний номер"
            rules={[{ required: true, message: 'Введіть серійний номер' }]}
          >
            <Input disabled={!!mdEditingItem} />
          </Form.Item>
          <Form.Item
            name="productId"
            label="Продукт (ID)"
            rules={[{ required: true, message: 'Введіть ID продукту' }]}
          >
            <Input disabled={!!mdEditingItem} />
          </Form.Item>
          <Form.Item
            name="customerId"
            label="Клієнт (ID)"
            rules={[{ required: true, message: 'Введіть ID клієнта' }]}
          >
            <Input disabled={!!mdEditingItem} />
          </Form.Item>
          <Form.Item
            name="installedAt"
            label="Дата встановлення"
            rules={[{ required: true, message: 'Оберіть дату встановлення' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabled={!!mdEditingItem} />
          </Form.Item>
          <Form.Item
            name="warrantyExpiresAt"
            label="Гарантія до"
            rules={[{ required: true, message: 'Оберіть дату закінчення гарантії' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="installationAddress"
            label="Адреса встановлення"
            rules={[{ required: true, message: 'Введіть адресу встановлення' }]}
          >
            <Input />
          </Form.Item>
          {mdEditingItem && (
            <>
              <Form.Item
                name="status"
                label="Статус"
                rules={[{ required: true, message: 'Оберіть статус' }]}
              >
                <Select options={MD_STATUS_OPTIONS.filter((mdOpt) => mdOpt.value !== '')} />
              </Form.Item>
              <Form.Item name="nextMaintenanceDue" label="Наступне ТО">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
